/**
 * Pila de pantallas.
 *
 * Vive fuera del componente y sin nada de React a propósito: la navegación ya
 * se rompió una vez de una forma que solo se veía en un móvil, y aquí se puede
 * ejercitar entera con tests.
 *
 * La idea: la pila la mandamos nosotros, y en el historial del navegador solo
 * va la **profundidad**. Cada `popstate` dice en qué nivel estamos y la pila se
 * recorta a eso, así que llegue como llegue el evento —nuestra flecha, el gesto
 * del móvil, dos toques seguidos— el resultado es el mismo.
 *
 * La versión anterior guardaba la vista entera en el estado del historial y
 * llamaba a `history.back()` a ciegas. Tenía dos agujeros: la entrada inicial no
 * llevaba estado, así que caer en ella reseteaba a la raíz vinieras de donde
 * vinieras; y como todas las pantallas comparten URL, dos "atrás" seguidos
 * saltaban dos niveles sin que nadie lo notara.
 */

export interface HistorialLike {
  pushState(estado: unknown, titulo: string): void;
  replaceState(estado: unknown, titulo: string): void;
  back(): void;
}

export interface EstadoHistorial {
  profundidad?: number;
}

export interface Navegacion<V> {
  pila(): V[];
  actual(): V;
  navegar(vista: V): V[];
  /** Sustituye la pantalla actual sin dejar rastro de la anterior. */
  reemplazar(vista: V): V[];
  /** Vuelve una pantalla. En la raíz no hace nada: no saca al usuario de la app. */
  volver(): void;
  /** Lo que hay que llamar desde el listener de `popstate`. */
  alRetroceder(estado: EstadoHistorial | null): V[];
  reiniciar(vista: V): V[];
}

export function crearNavegacion<V>(raiz: V, historial: HistorialLike): Navegacion<V> {
  let pila: V[] = [raiz];

  // Marcar la entrada inicial es lo que evita el reseteo silencioso al volver.
  historial.replaceState({ profundidad: 1 }, '');

  return {
    pila: () => pila,
    actual: () => pila[pila.length - 1] as V,

    navegar(vista) {
      historial.pushState({ profundidad: pila.length + 1 }, '');
      pila = [...pila, vista];
      return pila;
    },

    reemplazar(vista) {
      historial.replaceState({ profundidad: pila.length }, '');
      pila = [...pila.slice(0, -1), vista];
      return pila;
    },

    volver() {
      if (pila.length > 1) historial.back();
    },

    alRetroceder(estado) {
      const profundidad = Math.max(1, estado?.profundidad ?? 1);
      pila = pila.slice(0, profundidad);
      return pila;
    },

    reiniciar(vista) {
      historial.replaceState({ profundidad: 1 }, '');
      pila = [vista];
      return pila;
    },
  };
}
